import {
  CreateMappedSheetVersionInput,
  ImageAlignment,
  Scale,
} from 'records/MappedSheetVersion';
import { SheetVersion } from 'records/SheetVersion';
import { assign, Interpreter, Machine, State, StateMachine } from 'xstate';

export enum CalibrationMachineStatesEnum {
  aligning = 'aligning',
  cropping = 'cropping',
  done = 'done',
  initializing = 'initializing',
  selectingDrawing = 'selectingDrawing',
  scaling = 'scaling',
}

export enum CalibrationMachineEventsEnum {
  SAVE_SCALE = 'SAVE_SCALE',
  SAVE_CALIBRATION = 'SAVE_CALIBRATION',
  SAVE_CROP = 'SAVE_CROP',
  BACK_TO_DRAWING_SELECT = 'BACK_TO_DRAWING_SELECT',
  BACK_TO_SCALE = 'BACK_TO_SCALE',
  BACK_TO_CROP = 'BACK_TO_CROP',
}

enum Actions {
  UPDATE_CROP = 'UPDATE_CROP',
  UPDATE_ALIGNMENT = 'UPDATE_ALIGNMENT',
}

export type CalibrationMachineServices = {
  initializeContext: (documentid: string, pageid: string) => Promise<string>;
  saveCalibration: (
    imageCalibrationConfig: CreateMappedSheetVersionInput,
    sheetVersion: SheetVersion
  ) => void;
};

export type CalibrationStateMachine = StateMachine<
  CalibrationMachineContext,
  CalibrationMachineStates,
  CalibrationMachineEvents
>;

export type CalibrationMachineState = State<
  CalibrationMachineContext,
  CalibrationMachineEvents,
  CalibrationMachineStates
>;

export type CalibrationMachineService = Interpreter<
  CalibrationMachineContext,
  CalibrationMachineStates,
  CalibrationMachineEvents
>;

type CalibrationMachineProps = {
  requiredContext: {
    sheetVersion: SheetVersion;
    projectCenter: Coordinate;
    calibrationConfig: CalibrationMachineContext['calibrationConfig'];
  };
  services: CalibrationMachineServices;
};

export type CalibrationMachineContext = {
  calibrationConfig: {
    scale?: Scale;
    croppath: Point[];
    corners?: ImageAlignment;
    bearing: number;
    centerpoint: Coordinate;
  };
  sheetVersion: SheetVersion;
  pageUrl: string;
};

type CalibrationMachineStates = {
  states: {
    [key in CalibrationMachineStatesEnum]: Record<string, unknown>;
  };
};

type SaveScaleEvent = {
  type: CalibrationMachineEventsEnum.SAVE_SCALE;
  data: { scale: Scale };
};

type CalibrationMachineEvents =
  | SaveScaleEvent
  | {
      type: CalibrationMachineEventsEnum.SAVE_CROP;
      data: { croppath: Point[] };
    }
  | {
      type: CalibrationMachineEventsEnum.SAVE_CALIBRATION;
      data: {
        bearing: number;
        centerpoint: Coordinate;
        corners: ImageAlignment;
      };
    }
  | {
      type: CalibrationMachineEventsEnum.BACK_TO_SCALE;
      data: {
        croppath: Point[];
      };
    }
  | {
      type: CalibrationMachineEventsEnum.BACK_TO_CROP;
      data: {
        bearing: number;
        centerpoint: Coordinate;
        corners: ImageAlignment;
      };
    }
  | {
      type: CalibrationMachineEventsEnum.BACK_TO_DRAWING_SELECT;
      data: undefined;
    };

const createCalibrationMachine = ({
  requiredContext,
  services,
}: CalibrationMachineProps): CalibrationStateMachine =>
  Machine<
    CalibrationMachineContext,
    CalibrationMachineStates,
    CalibrationMachineEvents
  >(
    {
      id: 'calibration-machine',
      initial: CalibrationMachineStatesEnum.initializing,
      context: {
        calibrationConfig: requiredContext.calibrationConfig,
        pageUrl: '',
        sheetVersion: requiredContext.sheetVersion,
      },
      states: {
        [CalibrationMachineStatesEnum.initializing]: {
          invoke: {
            id: 'initializing',
            src: ({ sheetVersion: { documentid, pageid } }) =>
              services.initializeContext(documentid, pageid),
            onDone: {
              target: CalibrationMachineStatesEnum.scaling,
              actions: assign({
                pageUrl: (_, event) => event.data,
              }),
            },
            onError: {
              target: CalibrationMachineStatesEnum.done,
            },
          },
        },
        [CalibrationMachineStatesEnum.scaling]: {
          on: {
            [CalibrationMachineEventsEnum.SAVE_SCALE]: {
              target: CalibrationMachineStatesEnum.cropping,
              actions: assign<CalibrationMachineContext, SaveScaleEvent>({
                calibrationConfig: (
                  { calibrationConfig },
                  { data: { scale } }
                ) => ({ ...calibrationConfig, scale }),
              }),
            },
            [CalibrationMachineEventsEnum.BACK_TO_DRAWING_SELECT]:
              CalibrationMachineStatesEnum.selectingDrawing,
          },
        },
        [CalibrationMachineStatesEnum.cropping]: {
          on: {
            [CalibrationMachineEventsEnum.SAVE_CROP]: {
              target: CalibrationMachineStatesEnum.aligning,
              actions: Actions.UPDATE_CROP,
            },
            [CalibrationMachineEventsEnum.BACK_TO_SCALE]: {
              target: CalibrationMachineStatesEnum.scaling,
              actions: Actions.UPDATE_CROP,
            },
          },
        },
        [CalibrationMachineStatesEnum.aligning]: {
          on: {
            [CalibrationMachineEventsEnum.SAVE_CALIBRATION]: {
              target: CalibrationMachineStatesEnum.done,
              actions: [
                Actions.UPDATE_ALIGNMENT,
                ({ calibrationConfig, sheetVersion }) => {
                  if (calibrationConfig.scale && calibrationConfig.corners) {
                    const { scale, corners } = calibrationConfig;
                    services.saveCalibration(
                      {
                        ...calibrationConfig,
                        scale,
                        corners,
                      },
                      sheetVersion
                    );
                  }
                },
              ],
            },
            [CalibrationMachineEventsEnum.BACK_TO_CROP]: {
              target: CalibrationMachineStatesEnum.cropping,
              actions: Actions.UPDATE_ALIGNMENT,
            },
          },
        },
        [CalibrationMachineStatesEnum.selectingDrawing]: {
          type: 'final',
        },
        [CalibrationMachineStatesEnum.done]: {
          type: 'final',
        },
      },
    },
    {
      actions: {
        [Actions.UPDATE_CROP]: assign({
          calibrationConfig: ({ calibrationConfig }, event) => {
            if (
              event.type !== CalibrationMachineEventsEnum.SAVE_CROP &&
              event.type !== CalibrationMachineEventsEnum.BACK_TO_SCALE
            ) {
              return calibrationConfig;
            }

            return {
              ...calibrationConfig,
              croppath: event.data.croppath,
            };
          },
        }),
        [Actions.UPDATE_ALIGNMENT]: assign({
          calibrationConfig: ({ calibrationConfig }, event) => {
            if (
              event.type !== CalibrationMachineEventsEnum.SAVE_CALIBRATION &&
              event.type !== CalibrationMachineEventsEnum.BACK_TO_CROP
            ) {
              return calibrationConfig;
            }

            const { corners, centerpoint, bearing } = event.data;

            return {
              ...calibrationConfig,
              corners,
              centerpoint,
              bearing,
            };
          },
        }),
      },
    }
  );

export default createCalibrationMachine;
